// T-273 — Transport QZ Tray: wrapper sottile attorno al client `qz-tray` (UMD).
// Si collega all'helper QZ Tray in esecuzione sul PC (websocket locale), elenca
// le stampanti di sistema reali e stampa un'immagine su una stampante scelta
// PER NOME (indipendente dalla predefinita di Windows). Solo web/PWA: su nativo
// si usano i transport BLE/BT Classic.
//
// Modalità unsigned: NON imbarchiamo certificato/chiave privata nel bundle
// (sarebbe leggibile da chiunque). QZ Tray mostra una richiesta "Allow" una
// volta; con "Remember" le stampe successive sono silenziose.

import { Capacitor } from '@capacitor/core';
import type { QzConfig, QzStatic } from 'qz-tray';
import { buildQzPrintRequest, type QzPrintRequestInput } from './qz-print-request.js';

let qzPromise: Promise<QzStatic> | null = null;
let securityConfigured = false;

async function loadQz(): Promise<QzStatic> {
  if (Capacitor.isNativePlatform()) {
    throw new Error('QZ Tray è disponibile solo nella versione web (PWA).');
  }
  if (!qzPromise) {
    qzPromise = import('qz-tray').then((mod) => {
      const qz = (mod as unknown as { default?: QzStatic }).default ?? (mod as unknown as QzStatic);
      if (!securityConfigured) {
        // Unsigned: nessun certificato → QZ chiede "Allow" (con "Remember").
        qz.security.setCertificatePromise((_resolve, reject) => reject());
        // Firma vuota (nessuna chiave nel bundle).
        qz.security.setSignaturePromise(() => (resolve) => resolve());
        securityConfigured = true;
      }
      return qz;
    });
  }
  return qzPromise;
}

/** Apre (o riusa) la connessione all'helper QZ Tray locale. */
export async function qzConnect(): Promise<void> {
  const qz = await loadQz();
  if (qz.websocket.isActive()) return;
  try {
    await qz.websocket.connect();
  } catch (err) {
    throw new Error(
      'Impossibile connettersi a QZ Tray. Verifica che QZ Tray sia installato e ' +
      'in esecuzione sul computer (icona nella barra delle applicazioni). ' +
      `Dettaglio: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** Elenca le stampanti di sistema viste da QZ Tray. */
export async function qzListPrinters(): Promise<string[]> {
  await qzConnect();
  const qz = await loadQz();
  const found = await qz.printers.find();
  if (Array.isArray(found)) return found;
  return found ? [found] : [];
}

/** Stampa l'etichetta (immagine) su una stampante scelta per nome. */
export async function qzPrintLabel(input: QzPrintRequestInput): Promise<void> {
  await qzConnect();
  const qz = await loadQz();
  const req = buildQzPrintRequest(input);
  const config: QzConfig = qz.configs.create(req.printerName, req.config);
  await qz.print(config, req.data);
}

/** QZ Tray ha senso solo su web (PWA desktop). */
export function qzIsAvailable(): boolean {
  return !Capacitor.isNativePlatform();
}
